import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Share2, Loader2, Search } from "lucide-react";
import api, { fileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { ScrollArea } from "@/components/ui/scroll-area";

/** "Share" a resource with one of your connections. Opens a picker of accepted
 * connections; selecting one notifies that member with a link to the resource. */
export function ShareResourceButton({ resourceId, className }) {
  const [open, setOpen] = useState(false);
  const [conns, setConns] = useState(null);
  const [q, setQ] = useState("");
  const [busyId, setBusyId] = useState(null);

  useEffect(() => {
    if (!open) return;
    setConns(null);
    api.get("/network/connections").then(({ data }) => setConns(data || [])).catch(() => setConns([]));
  }, [open]);

  const share = async (c) => {
    setBusyId(c.user_id);
    try {
      await api.post(`/resources/${resourceId}/share`, { recipient_id: c.user_id });
      toast.success(`Shared with ${c.display_name}`);
      setOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not share");
    } finally { setBusyId(null); }
  };

  const filtered = (conns || []).filter((c) => (c.display_name || "").toLowerCase().includes(q.toLowerCase()));

  return (
    <>
      <Button className={`w-full ${className || ""}`} onClick={() => setOpen(true)} data-testid="resource-share-button">
        <Share2 className="mr-2 h-4 w-4" /> Share
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="share-resource-dialog">
          <DialogHeader>
            <DialogTitle>Share this resource</DialogTitle>
            <DialogDescription>Send this resource to one of your connections. They&apos;ll get a notification with a link.</DialogDescription>
          </DialogHeader>
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input className="pl-9" placeholder="Search connections" value={q} onChange={(e) => setQ(e.target.value)} data-testid="share-search" />
          </div>
          <ScrollArea className="max-h-72">
            {conns === null ? (
              <div className="py-8 text-center"><Loader2 className="mx-auto h-5 w-5 animate-spin text-muted-foreground" /></div>
            ) : filtered.length === 0 ? (
              <p className="py-8 text-center text-sm text-muted-foreground" data-testid="share-empty">
                {conns.length === 0 ? "You have no connections yet. Connect with members to share resources." : "No matching connections."}
              </p>
            ) : (
              <ul className="divide-y divide-border">
                {filtered.map((c) => (
                  <li key={c.user_id}>
                    <button
                      onClick={() => share(c)}
                      disabled={busyId === c.user_id}
                      className="flex w-full items-center gap-3 px-1 py-2.5 text-left transition-colors hover:bg-secondary/60 disabled:opacity-60"
                      data-testid={`share-connection-${c.user_id}`}
                    >
                      <Avatar className="h-9 w-9">
                        {c.headshot_url && <AvatarImage src={fileUrl(c.headshot_url)} alt="" />}
                        <AvatarFallback className="bg-teal-50 text-teal-700">{(c.display_name || "?")[0]}</AvatarFallback>
                      </Avatar>
                      <span className="flex-1 truncate font-[560] text-foreground">{c.display_name}</span>
                      {busyId === c.user_id ? <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" /> : <Share2 className="h-4 w-4 text-muted-foreground" />}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </ScrollArea>
        </DialogContent>
      </Dialog>
    </>
  );
}
